import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X,
  Zap,
  Hash,
  BarChart3,
  FileText,
  ArrowRight,
  DollarSign
} from 'lucide-react'

// Version number - increment this to show the announcement again to all users
const ANNOUNCEMENT_VERSION = '1.0'
const STORAGE_KEY = 'commercialization_announcement_seen'

interface FeatureItemProps {
  icon: React.ElementType
  color: string
  title: string
  description: string
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon: Icon, color, title, description }) => (
  <div className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100/80 transition-colors">
    <div
      className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${color}15` }}
    >
      <Icon size={14} style={{ color }} />
    </div>
    <div>
      <h4 className="text-gray-900 text-xs font-semibold">{title}</h4>
      <p className="text-gray-500 text-[11px] leading-snug">{description}</p>
    </div>
  </div>
)

interface CommercializationAnnouncementModalProps {
  onClose?: () => void
}

const CommercializationAnnouncementModal: React.FC<CommercializationAnnouncementModalProps> = ({
  onClose
}) => {
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false)

  // Check localStorage on mount to determine if we should show the modal
  useEffect(() => {
    const seenVersion = localStorage.getItem(STORAGE_KEY)
    if (seenVersion !== ANNOUNCEMENT_VERSION) {
      setIsVisible(true)
    }
  }, [])

  const markAsSeen = () => {
    localStorage.setItem(STORAGE_KEY, ANNOUNCEMENT_VERSION)
  }

  const handleClose = () => {
    markAsSeen()
    setIsVisible(false)
    onClose?.()
  }

  const handleOpenSponsorships = () => {
    markAsSeen()
    setIsVisible(false)
    navigate('/sponsorships')
    onClose?.()
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Decorative header gradient */}
        <div
          className="h-1.5"
          style={{
            background: 'linear-gradient(90deg, #8B5CF6 0%, #06B6D4 50%, #10B981 100%)'
          }}
        />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
        >
          <X size={16} />
        </button>

        {/* Content */}
        <div className="p-5">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-11 h-11 bg-gradient-to-br from-violet-500 to-emerald-500 rounded-xl mb-3 shadow-md shadow-violet-500/25">
              <DollarSign size={22} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              Commercialization Has Arrived
            </h2>
            <p className="text-xs text-gray-500">
              A complete toolkit to track, prove, and grow your sponsorship revenue.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-2 mb-5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
              What's new under Sponsorships
            </p>

            <FeatureItem
              icon={Zap}
              color="#8B5CF6"
              title="Activations"
              description="Link partners, assets, and tags to track EMV delivery against targets"
            />

            <FeatureItem
              icon={Hash}
              color="#06B6D4"
              title="Attribution Tags"
              description="Turn hashtags into trackable sponsorship metrics"
            />

            <FeatureItem
              icon={BarChart3}
              color="#F59E0B"
              title="Performance Dashboard"
              description="See pace, progress, and projections at a glance"
            />

            <FeatureItem
              icon={FileText}
              color="#10B981"
              title="Activation Reports"
              description="Generate detailed performance reports to share with internal stakeholders and partners"
            />
          </div>

          {/* Footer tagline */}
          <p className="text-center text-xs text-gray-600 font-medium mb-4">
            Time to show sponsors exactly what they're getting.
          </p>

          {/* Actions */}
          <button
            onClick={handleOpenSponsorships}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs rounded-lg font-semibold transition-colors shadow-md shadow-violet-500/25"
          >
            Open Sponsorships
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default CommercializationAnnouncementModal
